import { Icon, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Text } from './typography';
import styles from './UserSelector.module.scss';
import { closeModalComponents } from '../utilities/modal';
import { useWorkspacePermissions } from '../providers/WorkspacePermissionsProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';
import SimpleUserCard from './SimpleUserCard';
import { useWorkspace } from '../providers/WorkspaceProvider';
import { useWorkspaceMutations } from '../hooks/addUserToWorkspace';
import { useModal } from '../providers/ModalProvider';
import { useCurrentUser } from '../providers/UserProvider';
import { customColors, sortAlphabeticalOrder } from '../utilities';
import { useQueryParams } from '../hooks/misc';
import classNames from 'classnames';
import PortalMenu from '../utilities/Portal/PortalMenu';

export default function UserSelector({
  selected,
  onSelectionChanged,
  type,
  className = null,
  onClose,
  userQuery = '',
  disableRemove = false,
  usersToLeaveOut = [],
  readOnly = false,
  cardProps = { largeAvatar: false, weight: 'regular' },
  otherWorkspace = null,
  iconDisplay = false,
  border = true,
  modalClassName,
  menuStyle = {},
}) {
  const [search, setSearch] = useState('');
  const {
    allWorkspaceUsers,
    allUsers,
    companyAdmins,
    isWorkspaceAdmin,
    refetchAllUsers,
  } = useWorkspacePermissions();
  const { t } = useTranslation();
  const { modalIsOpen } = useModal();
  const [modalOpen, setModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { workspace, companyPermissions, refetchWorkspacePermission } =
    useWorkspace();
  const { addUser } = useWorkspaceMutations();
  const { openConfirmationModal } = useModal();
  const { isAdmin } = useCurrentUser();
  const { getParam } = useQueryParams();
  const allWorkspacesParam = getParam('allWorkspaces') === 'TRUE';
  const showAllUsers = useMemo(
    () =>
      type === 'all' ||
      userQuery === 'all' ||
      (allWorkspacesParam &&
        !modalIsOpen &&
        type !== 'workspace' &&
        userQuery !== 'workspace'),
    [allWorkspacesParam, modalIsOpen, type, userQuery],
  );

  const ref = useRef(null);
  const searchRef = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => {
    closeModalComponents(ref, setModalOpen);
  }, [ref]);

  useEffect(() => {
    if (!modalOpen) {
      setSearch('');
    } else {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [modalOpen]);

  const workspaceFilteredUsers = useMemo(() => {
    let workspaceUsers = [...companyAdmins];

    if (!!otherWorkspace?.id) {
      const o = companyPermissions
        ?.filter((p) => p.workspaceId === otherWorkspace?.id)
        ?.map(({ user }) => allUsers?.find((u) => u.id === user.id) || user);
      if (o && o.length) workspaceUsers = workspaceUsers.concat(o);
    } else if (allWorkspaceUsers && allWorkspaceUsers.length) {
      workspaceUsers = workspaceUsers.concat(
        allWorkspaceUsers.map((u) => allUsers?.find((a) => a.id === u.id) || u),
      );
    }

    const userMap = new Map();
    workspaceUsers.forEach((user) => {
      userMap.set(user.id, user);
    });

    const uniqueWorkspaceUsers = Array.from(userMap.values());

    const filteredUsers = search?.length
      ? uniqueWorkspaceUsers.filter((user) => {
          const userString = `${user.firstName} ${user.lastName}`;
          const roleString =
            user?.workRoles?.map((r) => r.title)?.join(',') ?? '';
          return (
            userString.toLowerCase().includes(search.toLowerCase()) ||
            roleString.toLowerCase().includes(search.toLowerCase())
          );
        })
      : uniqueWorkspaceUsers;

    const f = filteredUsers?.filter(
      (u) => !usersToLeaveOut?.some((s) => s.id === u.id),
    );

    const sorted = sortAlphabeticalOrder(f, 'firstName');
    return sorted || f;
  }, [
    allUsers,
    allWorkspaceUsers,
    companyAdmins,
    companyPermissions,
    otherWorkspace?.id,
    search,
    usersToLeaveOut,
  ]);

  const allFilteredUsers = useMemo(() => {
    const u = (
      search?.length
        ? allUsers?.filter((user) => {
            const userString = `${user.firstName} ${user.lastName}`;
            const roleString =
              user?.workRoles?.map((r) => r.title)?.join(',') ?? '';
            return (
              userString.toLowerCase().includes(search.toLowerCase()) ||
              roleString.toLowerCase().includes(search.toLowerCase())
            );
          })
        : allUsers || []
    ).filter((u) => !usersToLeaveOut?.some((s) => s.id === u.id));
    const sorted = sortAlphabeticalOrder(u) || u;
    return sorted;
  }, [search, allUsers, usersToLeaveOut]);

  const usersWithAddOption = useMemo(() => {
    const u = allFilteredUsers?.filter(
      (user) =>
        !workspaceFilteredUsers?.some((wsUser) => wsUser.id === user.id),
    );
    const sorted = sortAlphabeticalOrder(u);
    return sorted || u;
  }, [allFilteredUsers, workspaceFilteredUsers]);

  const filteredUsers = useMemo(
    () =>
      showAllUsers ? allFilteredUsers || [] : workspaceFilteredUsers || [],
    [allFilteredUsers, showAllUsers, workspaceFilteredUsers],
  );

  useEffect(() => {
    if (!modalOpen) return;
    closeModalComponents(ref, setModalOpen);
  }, [modalOpen]);

  const inWorkspace = useMemo(() => {
    const userInfo = selected
      ? allUsers?.find((a) => a.id === selected?.id)
      : null;
    if (!userInfo) return;
    const wsId = otherWorkspace?.id || workspace?.id;
    const userWorkspaces = userInfo.workspaces?.map((w) => w.id);
    return showAllUsers || userInfo.role === 'ADMIN'
      ? true
      : userWorkspaces?.includes(wsId);
  }, [allUsers, otherWorkspace?.id, selected, showAllUsers, workspace?.id]);

  const handleModalOpen = (e) => {
    if (!readOnly) setModalOpen(true);
    const rect = containerRef.current.getBoundingClientRect();
    setMenuPosition(
      !modalOpen
        ? {
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            ...menuStyle,
          }
        : {},
    );
  };

  return (
    <div
      className={classNames([styles.relative, styles.container])}
      ref={containerRef}
    >
      {iconDisplay ? (
        <div className={className || styles.iconContainer}>
          {selected ? (
            <img
              src={selected.avatarUrl}
              className={styles.avatarSm}
              alt="avatar"
              onClick={handleModalOpen}
            />
          ) : (
            <Icon
              className={styles.hover}
              baseClassName="material-icons-outlined"
              sx={{
                fontSize: '1.2rem',
                color: customColors.BLUE,
                cursor: 'pointer',
              }}
              onClick={handleModalOpen}
            >
              person_add
            </Icon>
          )}
        </div>
      ) : (
        <div className={styles.relative}>
          <div className={styles.selector}>
            {!!selected || readOnly ? (
              <div className={className}>
                <div className={styles.flexRow}>
                  <SimpleUserCard
                    color={inWorkspace ? 'primary' : 'red'}
                    disableClick
                    user={selected}
                    weight={cardProps.weight}
                    largeAvatar={cardProps.largeAvatar}
                    mediumAvatar={cardProps.mediumAvatar}
                  />
                  {!!selected?.id && !inWorkspace ? (
                    <Tooltip
                      title={`No Access to ${
                        otherWorkspace?.title || workspace?.title
                      } `}
                    >
                      <Icon
                        style={{
                          color: customColors.RED_LIGHT,
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                        }}
                        onClick={() => {
                          if (isAdmin || isWorkspaceAdmin) {
                            openConfirmationModal({
                              variant: 'warning',
                              buttonText: 'Add to Workspace',
                              title: `User Workspace Permission`,
                              description: `${selected.firstName} ${
                                selected.lastName
                              } does not have workspace permissions for ${
                                otherWorkspace?.title || workspace.title
                              } and will not have access. Would you like to add them?`,
                              onSubmit: () => {
                                addUser({
                                  userId: selected?.id,
                                  workspaceId:
                                    otherWorkspace?.id || workspace?.id,
                                }).then(async () => {
                                  await refetchWorkspacePermission();
                                  await onSelectionChanged(selected);
                                  await refetchAllUsers();
                                });
                              },
                            });
                          }
                        }}
                      >
                        error
                      </Icon>
                    </Tooltip>
                  ) : null}
                  {(!readOnly || disableRemove) && (
                    <Icon
                      baseClassName="material-icons-outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                      }}
                      sx={{ fontSize: '1.2rem', cursor: 'pointer' }}
                    >
                      close
                    </Icon>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={!!border ? styles.buttonContainer : styles.noBorder}
                onClick={handleModalOpen}
              >
                <Icon
                  style={{ color: customColors.SECONDARY_LIGHT }}
                  baseClassName="material-icons-outlined"
                >
                  person
                </Icon>
                <Text size="md" noSelect color="secondary" noMargin>
                  {t(keys.action.ADD, {
                    variable: t(keys.common[type]) || t(keys.common.USER),
                  })}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {modalOpen && (
        <PortalMenu open={modalOpen} position={menuPosition}>
          <div
            ref={ref}
            className={classNames(
              !!iconDisplay ? styles.iconModal : styles.modalContainer,
              modalClassName,
            )}
          >
            <div className={styles.modalHeader}>
              <Form.Control
                ref={searchRef}
                className={styles.input}
                type="text"
                placeholder={t(keys.action.SEARCH, {
                  variable: t(keys.common.USERS),
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Icon
                baseClassName="material-icons-outlined"
                onClick={() => setModalOpen(false)}
              >
                close
              </Icon>
            </div>
            {!filteredUsers?.length ? (
              <div className={styles.userListContainer}>
                <Text noMargin textAlign="center">
                  {t(keys.utilities.NO_USERS_FOUND)}
                </Text>
              </div>
            ) : (
              <div className={styles.userListContainer}>
                {filteredUsers?.map((user) => (
                  <div
                    key={user.id}
                    className={styles.userContainer}
                    onClick={() => {
                      onSelectionChanged(user);
                      setModalOpen(false);
                    }}
                  >
                    <SimpleUserCard
                      user={user}
                      size="md"
                      weight="semiBold"
                      smallText={user?.workRoles
                        ?.map(({ title }) => title)
                        ?.join(', ')}
                      smallTextSize="xs"
                      disableClick
                      wordsToHighlight={search ? [search.toLowerCase()] : []}
                    />
                  </div>
                ))}
                {!!usersWithAddOption?.length && isAdmin && !showAllUsers ? (
                  <div
                    className={styles.userListContainer}
                    style={{ border: 'none' }}
                  >
                    <div className={styles.sectionLine} />
                    <Text
                      weight="semiBold"
                      size="sm"
                      textAlign="center"
                      noMargin
                    >{`Invite to ${
                      otherWorkspace?.title || workspace?.title
                    }`}</Text>
                    <div className={styles.sectionLine} />

                    {usersWithAddOption?.map((user) => (
                      <div
                        key={user.id}
                        className={styles.inviteUserContainer}
                        onClick={() => {
                          openConfirmationModal({
                            variant: 'warning',
                            title: 'Add User to Workspace',
                            description: `Are you sure you want to add ${
                              user.firstName
                            } ${user.lastName} to ${
                              otherWorkspace?.title || workspace?.title
                            }?`,
                            onSubmit: async () => {
                              await addUser({
                                userId: user.id,
                                workspaceId:
                                  otherWorkspace?.id || workspace?.id,
                              }).then(async () => {
                                await onSelectionChanged(user);
                                await refetchWorkspacePermission();
                                await refetchAllUsers();
                              });
                            },
                          });
                        }}
                      >
                        <SimpleUserCard
                          user={user}
                          size="md"
                          weight="semiBold"
                          smallText={user?.workRoles
                            ?.map(({ title }) => title)
                            ?.join(', ')}
                          smallTextSize="xs"
                          disableClick
                          wordsToHighlight={
                            search ? [search.toLowerCase()] : []
                          }
                        />
                        <Icon className={styles.addIcon}>add</Icon>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </PortalMenu>
      )}
    </div>
  );
}
